<script setup lang="ts">
import { computed, watch } from "vue";
import CouponEntry from "@/components/Account/Checkout/CouponEntry.vue";
import type { Plan } from "@/stores/planStore";
import FrequencyDetailsRadioGroupSimple from "@/components/Account/Checkout/FrequencyDetailsRadioGroupSimple.vue";
import { frequencies } from "@/components/Account/Checkout/types.ts";
import { useCheckoutStore } from "@/components/Account/Checkout/checkoutStore.ts";
import { storeToRefs } from "pinia";
import { CheckCircleIcon } from "@heroicons/vue/20/solid";

const checkoutStore = useCheckoutStore();
const {
    upcomingInvoice,
    loadingInvoice,
    discountPercent,
    discountAmount,
    couponCode,
} = storeToRefs(checkoutStore);
const { applyCoupon } = checkoutStore;

const props = defineProps<{
    plan?: Plan | null;
    frequencyOptions?: [];
    hideTaxes?: boolean;
    hideTotal?: boolean;
    buttonText?: string;
    hideNotifications?: boolean;
    hideTermsAndPolicies?: boolean;
    isLoading?: boolean;
    error?: string | null;
    onRetry?: () => void;
    notifications?: boolean;
    renewalOffer?: boolean;
    couponCode?: string;
}>();

const emit = defineEmits<{
    selectPlan: [];
    frequencyChange: [frequency: string];
    'update:notifications': [value: boolean];
}>();

const handleSubmit = () => {
    emit('selectPlan');
};

const notificationsModel = computed({
    get: () => props.notifications ?? true,
    set: (value: boolean) => emit('update:notifications', value)
});

const billingText = computed(() => {
    if (!props.plan) return '';
    const frequency = frequencies[props.plan.frequency];
    if (!frequency) return `Automatically billed at $${props.plan.price.toFixed(2)}. Cancel anytime.`;

    return `Automatically billed ${frequency.alt} at $${props.plan.price.toFixed(2)}. Cancel anytime.`;
});

const lockedFrequencyLabel = computed(() => {
    const freq = props.plan?.frequency;
    const freqMeta = freq ? frequencies[freq] : undefined;
    return freqMeta?.alt ?? freq ?? '';
});

const discountBadge = computed(() => {
    if (discountPercent.value) {
        return `${discountPercent.value}%`;
    }
    if (discountAmount.value) {
        return `$${(discountAmount.value / 100).toFixed(2)}`;
    }
    return '';
});

const subtotal = computed(() => {
    if (!props.plan) return 0;
    return props.plan.price;
});

const taxes = computed(() => {
    return upcomingInvoice.value?.tax ? (upcomingInvoice.value.tax / 100) : 0;
});

const discounts = computed(() => {
    if (upcomingInvoice.value?.total_discount_amounts?.[0]?.amount) {
        return upcomingInvoice.value.total_discount_amounts[0].amount / 100;
    }
    // Fallback: compute from plan price when upcomingInvoice is unavailable (e.g. register page, unauthenticated)
    if (!props.plan) return 0;
    if (discountPercent.value > 0) {
        return (props.plan.price * discountPercent.value) / 100;
    }
    if (discountAmount.value > 0) {
        return discountAmount.value / 100;
    }
    return 0;
});

const total = computed(() => {
    if (!props.plan || !upcomingInvoice.value) return props.plan?.price || 0;
    return upcomingInvoice.value.total === 0 ? 0 : (upcomingInvoice.value.total / 100);
});

watch(
    () => props.couponCode,
    async (code) => {
        if (!code) return;
        if (couponCode.value === code && (discountPercent.value > 0 || discountAmount.value > 0)) {
            return;
        }
        couponCode.value = code;
        try {
            await applyCoupon(code);
        } catch (e) {
            console.error("Failed to apply coupon", e);
        }
    },
    { immediate: true }
);
</script>

<template>
    <div class="bg-white/80 shadow-lg rounded-md p-8 w-full max-w-xl">
        <!-- Loading State -->
        <div v-if="isLoading" class="animate-pulse space-y-6">
            <!-- Plan Type Section -->
            <div>
                <div class="h-4 w-20 bg-gray-200 rounded mb-2"></div>
                <div class="h-8 w-48 bg-gray-200 rounded"></div>
            </div>
            <!-- Subtotal Section -->
            <div class="border-t-2 border-gray-200 pt-4 space-y-2">
                <div class="flex items-center justify-between">
                    <div class="h-4 w-16 bg-gray-200 rounded"></div>
                    <div class="h-4 w-16 bg-gray-200 rounded"></div>
                </div>
                <div class="flex items-center justify-between">
                    <div class="h-3 w-12 bg-gray-200 rounded"></div>
                    <div class="h-3 w-12 bg-gray-200 rounded"></div>
                </div>
            </div>
            <!-- Total Section -->
            <div class="border-t-2 border-b-2 border-gray-200 py-4">
                <div class="flex items-center justify-between">
                    <div class="h-5 w-16 bg-gray-200 rounded"></div>
                    <div class="h-5 w-20 bg-gray-200 rounded"></div>
                </div>
            </div>
            <!-- Button Section -->
            <div class="space-y-4">
                <div class="h-4 w-full bg-gray-200 rounded"></div>
                <div class="h-12 w-full bg-gray-200 rounded"></div>
                <div class="h-8 w-full bg-gray-200 rounded"></div>
                </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center space-y-4">
            <p class="text-red-500">{{ error }}</p>
            <button
                v-if="onRetry"
                @click="onRetry"
                class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
                Retry
            </button>
        </div>

        <!-- Empty State (no plan data) -->
        <div v-else-if="!plan" class="text-center">
            <p class="text-gray-500">Unable to load plan information</p>
        </div>

        <!-- Normal Content -->
        <div v-else class="space-y-6">
            <div>
                <p class="uppercase text-gray-600 font-semibold text-sm">
                    Plan Type
                </p>
                <h2 class="font-bold text-xl">{{ plan.name }}</h2>
            </div>
            <div class="space-y-2">
                <template v-if="renewalOffer">
                    <p class="text-sm font-semibold">Billing frequency</p>
                    <div
                        class="flex items-center gap-1 rounded-md px-3 py-2"
                    >
                      <div class="flex items-center gap-2">
                        <div class="relative" aria-hidden="true">
                            <span
                                class="block rounded-full bg-white h-5 w-5 border border-blue-700"
                            ></span>
                            <CheckCircleIcon
                                class="h-5 w-5 text-blue-700 absolute top-0 left-0"
                                aria-hidden="true"
                            />
                        </div>
                        <span class="text-sm capitalize text-gray-900">
                            {{ lockedFrequencyLabel }}
                        </span>
                      </div>
                        <span
                            v-if="discountBadge"
                            class="text-sm text-green-700 italic font-medium"
                        >
                            {{ discountBadge }} off regular {{ lockedFrequencyLabel }} pricing
                        </span>
                    </div>
                    <p class="text-xs italic">{{ billingText }}</p>
                </template>
                <template v-else>
                    <FrequencyDetailsRadioGroupSimple  />
                    <p class="text-xs italic">{{ billingText }}</p>
                </template>
            </div>
            <div v-if="!renewalOffer">
               <coupon-entry />
            </div>
            <div class="border-t-2 border-gray-200 pt-4 space-y-2">
                <div class="flex items-center justify-between">
                    <p class="text-sm font-semibold">Subtotal</p>
                    <p class="font-semibold">${{ subtotal.toFixed(2) }}</p>
                </div>
                <div v-if="discounts > 0" class="flex items-center justify-between">
                    <p class="text-xs font-semibold text-gray-500">Discounts</p>
                    <div v-if="loadingInvoice" class="h-3 w-12 bg-gray-200 rounded animate-pulse"></div>
                    <p v-else class="font-semibold text-sm text-gray-500">-${{ discounts.toFixed(2) }}</p>
                </div>
                <div v-if="!hideTaxes" class="flex items-center justify-between">
                    <p class="text-xs font-semibold text-gray-500">Taxes</p>
                    <div v-if="loadingInvoice" class="h-3 w-12 bg-gray-200 rounded animate-pulse"></div>
                    <p v-else class="font-semibold text-sm text-gray-500">${{ taxes.toFixed(2) }}</p>
                </div>
            </div>
            <div v-if="!hideTotal" class="border-t-2 border-b-2 border-gray-200 py-4 space-y-2">
                <div class="flex items-center justify-between">
                    <p class="text-sm font-bold">TOTAL</p>
                    <div v-if="loadingInvoice" class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
                    <p v-else class="font-bold">${{ total.toFixed(2) }}</p>
                </div>
            </div>
            <div v-if="(!hideNotifications && !renewalOffer) || !hideTermsAndPolicies" class="space-y-4">
                <div v-if="!hideNotifications && !renewalOffer" class="flex items-center gap-1 text-sm">
                    <input type="checkbox" id="checkbox1" v-model="notificationsModel" />
                    <label for="checkbox1"
                        >Get product and discount notifications</label
                    >
                </div>
                <button class="button main w-full rounded-md" @click="handleSubmit">
                    {{ buttonText || 'Complete Purchase' }}
                </button>
                <div v-if="!hideTermsAndPolicies" class="text-xs">
                    By pressing "Proceed to Payment" you agree to our
                    <a href="https://cloverlearning.com/terms-of-use" class="text-blue-700 underline" target="_blank">Terms of Use</a> and
                    <a href="https://cloverlearning.com/privacy-policy" class="text-blue-700 underline" target="_blank">Privacy Policy</a>.
                </div>
            </div>
            <!-- Show button standalone if both sections are hidden -->
            <button v-else class="button main w-full rounded-md" @click="handleSubmit">
                {{ buttonText || 'Complete Purchase' }}
            </button>
        </div>
    </div>
</template>
